'use client';

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import type { Translations } from "@/i18n/messages";
import type { CourtConfiguration, OpenPlaySession, VenueData } from "@/types/venue";
import { detectVenueSearchMode, VenueSearchMode } from "@/lib/venue-search";
import { buildVenueSeoPath } from "@/lib/venue-seo";
import { fetchVenues } from "@/services/venues";

export type HomeVenuesMessages = Translations["homeVenues"];

interface VenuesNearMeSectionProps {
  messages: HomeVenuesMessages;
}

interface VenuesExplorerProps {
  messages: HomeVenuesMessages;
  initialQuery?: string;
  focusVenueId?: number | null;
}

interface UserLocation {
  latitude: number;
  longitude: number;
}

interface UseVenuesSearchOptions {
  itemsPerPage?: number;
  autoLocate?: boolean;
  initialQuery?: string;
}

interface UseVenuesSearchResult {
  pendingQuery: string;
  activeQuery: string;
  setPendingQuery: (value: string) => void;
  submitSearch: () => void;
  clearFilters: () => void;
  venues: VenueData[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  requestLocation: () => void;
  isRequestingLocation: boolean;
  hasRequested: boolean;
  latitude: number | null;
  longitude: number | null;
}

const AMENITIES_OPTIONS = [
  "Lighting",
  "Restrooms",
  "Water Stations",
  "Seating",
  "Pro Shop",
  "Lockers",
  "Showers",
  "Food/Drinks",
  "Parking",
  "Wheelchair Accessible",
  "Shade Structures",
  "Ball Machine",
  "Lessons Available",
];

const DEFAULT_ITEMS_PER_PAGE = 10;
const EXPLORER_ITEMS_PER_PAGE = 10;
const VENUE_PHOTO_BASE_URL = "https://whosplays.s3.us-east-1.amazonaws.com/courts/";
const FALLBACK_IP_COORDINATES = Object.freeze({
  latitude: 8,
  longitude: 8,
});
const LOCATION_STORAGE_KEY = "whoplays:user-location";

export function VenuesNearMeSection({ messages }: VenuesNearMeSectionProps) {
  const {
    pendingQuery,
    activeQuery,
    setPendingQuery,
    submitSearch,
    clearFilters,
    venues,
    isLoading,
    error,
    hasMore,
    loadMore,
    requestLocation,
    isRequestingLocation,
    hasRequested,
    latitude,
    longitude,
  } = useVenuesSearch(messages, { itemsPerPage: DEFAULT_ITEMS_PER_PAGE, autoLocate: false });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submitSearch();
    },
    [submitSearch],
  );

  const showPrompt = !hasRequested && !isLoading && venues.length === 0;
  const showNoResults = hasRequested && !isLoading && venues.length === 0;

  return (
    <section className="space-y-8 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-2xl shadow-primary/10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">{messages.title}</h2>
          <p className="max-w-2xl text-muted-foreground">{messages.description}</p>
        </div>
        <Link
          href="/venues"
          className="inline-flex items-center justify-center self-start rounded-full border border-border px-5 py-2 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {messages.viewAllVenues}
        </Link>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={requestLocation}
          disabled={isRequestingLocation}
          className="inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRequestingLocation ? messages.locating : messages.useMyLocation}
        </button>
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3 sm:flex-row">
          <input
            value={pendingQuery}
            onChange={(event) => setPendingQuery(event.target.value)}
            placeholder={messages.searchPlaceholder}
            className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label={messages.searchAriaLabel}
          />
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {messages.searchCta}
          </button>
          {pendingQuery.trim().length > 0 || activeQuery.length > 0 ? (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {messages.clearFilters}
            </button>
          ) : null}
        </form>
      </div>

      {error ? <p className="rounded-2xl border border-destructive/60 bg-destructive/10 p-4 text-sm text-destructive">{error}</p> : null}

      {showPrompt ? (
        <p className="rounded-2xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          {messages.promptLocation}
        </p>
      ) : null}

      {showNoResults ? (
        <p className="rounded-2xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">{messages.noResults}</p>
      ) : null}

      <div className="flex gap-4 overflow-x-auto pb-2">
        {venues.map((venue) => {
          const distance = getDistanceMiles(venue, latitude, longitude);
          const distanceLabel =
            distance !== null ? messages.distanceAway.replace("{distance}", formatDistance(distance)) : null;
          const status = getVenueStatus(venue, messages);

          return (
            <VenueTeaserCard
              key={venue.id}
              venue={venue}
              messages={messages}
              distanceLabel={distanceLabel}
              status={status}
            />
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
            <span className="size-2 animate-pulse rounded-full bg-primary" />
            {messages.loading}
          </span>
        </div>
      ) : null}

      {hasMore && venues.length > 0 ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => {
              if (isLoading) return;
              loadMore();
            }}
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? messages.loadingMore : messages.loadMore}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function VenuesExplorer({ messages, initialQuery = "", focusVenueId = null }: VenuesExplorerProps) {
  const {
    pendingQuery,
    activeQuery,
    setPendingQuery,
    submitSearch,
    clearFilters,
    venues,
    isLoading,
    error,
    hasMore,
    loadMore,
    requestLocation,
    isRequestingLocation,
    hasRequested,
    latitude,
    longitude,
  } = useVenuesSearch(messages, {
    itemsPerPage: EXPLORER_ITEMS_PER_PAGE,
    autoLocate: false,
    initialQuery,
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submitSearch();
    },
    [submitSearch],
  );

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoading) {
            loadMore();
          }
        });
      },
      { rootMargin: "200px" },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMore]);

  const focusHandledRef = useRef(false);

  useEffect(() => {
    focusHandledRef.current = false;
  }, [focusVenueId]);

  useEffect(() => {
    if (!focusVenueId || focusHandledRef.current || venues.length === 0) return;
    if (typeof window === "undefined") return;

    const element = document.querySelector<HTMLElement>(`[data-venue-id="${focusVenueId}"]`);
    if (!element) return;

    focusHandledRef.current = true;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.classList.add("ring-2", "ring-primary");

    const timeout = window.setTimeout(() => {
      element.classList.remove("ring-2", "ring-primary");
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [venues, focusVenueId]);

  const showPrompt = !hasRequested && !isLoading && venues.length === 0;
  const showNoResults = hasRequested && !isLoading && venues.length === 0;

  const { pageTitle, pageDescription, title, description } = messages as {
    pageTitle?: string;
    pageDescription?: string;
    title: string;
    description: string;
  };

  return (
    <section className="space-y-6 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-2xl shadow-primary/10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{pageTitle ?? title}</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            {pageDescription ?? description}
          </p>
        </div>
        <button
          type="button"
          onClick={requestLocation}
          disabled={isRequestingLocation}
          className="inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRequestingLocation ? messages.locating : messages.useMyLocation}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
        <input
          value={pendingQuery}
          onChange={(event) => setPendingQuery(event.target.value)}
          placeholder={messages.searchPlaceholder}
          className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          aria-label={messages.searchAriaLabel}
        />
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {messages.searchCta}
        </button>
        {pendingQuery.trim().length > 0 || activeQuery.length > 0 ? (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {messages.clearFilters}
          </button>
        ) : null}
      </form>

      {error ? <p className="rounded-2xl border border-destructive/60 bg-destructive/10 p-4 text-sm text-destructive">{error}</p> : null}

      {showPrompt ? (
        <p className="rounded-2xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          {messages.promptLocation}
        </p>
      ) : null}

      {showNoResults ? (
        <p className="rounded-2xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">{messages.noResults}</p>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        {venues.map((venue) => {
          const distance = getDistanceMiles(venue, latitude, longitude);
          const distanceLabel =
            distance !== null ? messages.distanceAway.replace("{distance}", formatDistance(distance)) : null;
          const status = getVenueStatus(venue, messages);
          const todaysSessions = getTodaysSessions(venue);

          return (
            <VenueDetailsCard
              key={venue.id}
              venue={venue}
              messages={messages}
              distanceLabel={distanceLabel}
              status={status}
              todaysSessions={todaysSessions}
            />
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
            <span className="size-2 animate-pulse rounded-full bg-primary" />
            {messages.loading}
          </span>
        </div>
      ) : null}

      <div ref={loadMoreRef} aria-hidden="true" />

      {hasMore && !isLoading ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {messages.loadMore}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function useVenuesSearch(messages: HomeVenuesMessages, options: UseVenuesSearchOptions = {}): UseVenuesSearchResult {
  const { itemsPerPage = DEFAULT_ITEMS_PER_PAGE, autoLocate = false, initialQuery = "" } = options;

  const [pendingQuery, setPendingQueryState] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery.trim());
  const [venues, setVenues] = useState<VenueData[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [searchMode, setSearchMode] = useState<VenueSearchMode | null>(null);
  const isLoadingRef = useRef(false);

  const latitude = userLocation?.latitude ?? null;
  const longitude = userLocation?.longitude ?? null;
  const hasCoordinates = latitude !== null && longitude !== null;
  const hasQuery = query.length > 0;
  const allowFallbackLocation = (searchMode ?? null) !== "name";
  const shouldFallbackToIp = allowFallbackLocation && !hasCoordinates && !hasQuery;
  const fallbackLatitude = shouldFallbackToIp ? FALLBACK_IP_COORDINATES.latitude : null;
  const fallbackLongitude = shouldFallbackToIp ? FALLBACK_IP_COORDINATES.longitude : null;
  const effectiveLatitude = hasCoordinates ? latitude : fallbackLatitude;
  const effectiveLongitude = hasCoordinates ? longitude : fallbackLongitude;
  const effectiveHasCoordinates = effectiveLatitude !== null && effectiveLongitude !== null;

  const persistUserLocation = useCallback((coords: UserLocation) => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(coords));
    } catch {
      // Ignore storage failures (private mode, etc.)
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.sessionStorage.getItem(LOCATION_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Partial<UserLocation>;
      if (
        parsed &&
        typeof parsed.latitude === "number" &&
        Number.isFinite(parsed.latitude) &&
        typeof parsed.longitude === "number" &&
        Number.isFinite(parsed.longitude)
      ) {
        const coords = { latitude: parsed.latitude, longitude: parsed.longitude };
        setUserLocation(coords);
        setSearchMode((current) => current ?? "location");
        setHasRequested(true);
      }
    } catch {
      // Ignore malformed storage payloads
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (userLocation) return;
    if (!("geolocation" in navigator)) return;
    if (!("permissions" in navigator) || typeof navigator.permissions?.query !== "function") return;

    let cancelled = false;
    let removeListener: (() => void) | undefined;

    const hydrateGrantedLocation = () => {
      if (cancelled || userLocation) return;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
          setUserLocation(coords);
          setSearchMode((current) => current ?? "location");
          setHasRequested(true);
          persistUserLocation(coords);
        },
        () => undefined,
        { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000 },
      );
    };

    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((status) => {
        if (cancelled) return;

        const handleChange = () => {
          if (status.state === "granted") {
            hydrateGrantedLocation();
          }
        };

        if (status.state === "granted") {
          hydrateGrantedLocation();
        }

        if (typeof status.addEventListener === "function") {
          status.addEventListener("change", handleChange);
          removeListener = () => status.removeEventListener("change", handleChange);
        } else {
          const previous = status.onchange;
          status.onchange = handleChange;
          removeListener = () => {
            status.onchange = previous ?? null;
          };
        }
      })
      .catch(() => {
        // Ignore permission query errors (e.g., Safari < 16)
      });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [userLocation, persistUserLocation]);

  useEffect(() => {
    if (!searchMode && query) {
      setSearchMode(detectVenueSearchMode(query));
    }
  }, [searchMode, query]);

  useEffect(() => {
    if (!autoLocate || typeof window === "undefined" || !("geolocation" in navigator)) return;

    let cancelled = false;
    setIsRequestingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        setUserLocation(coords);
        setSearchMode((current) => current ?? "location");
        setHasRequested(true);
        setIsRequestingLocation(false);
        persistUserLocation(coords);
      },
      () => {
        if (cancelled) return;
        setIsRequestingLocation(false);
      },
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000 },
    );

    return () => {
      cancelled = true;
    };
  }, [autoLocate, persistUserLocation]);

  useEffect(() => {
    const controller = new AbortController();
    const modeForFetch: VenueSearchMode | null =
      searchMode ?? (effectiveHasCoordinates ? "location" : null);
    const shouldFetch =
      modeForFetch === "name"
        ? Boolean(query)
        : modeForFetch === "location"
          ? Boolean(query) || effectiveHasCoordinates
          : false;

    if (!shouldFetch) {
      if (page === 0) {
        setVenues([]);
        setHasMore(false);
      }
      setIsLoading(false);
      isLoadingRef.current = false;
      return () => controller.abort();
    }

    async function loadVenues() {
      setIsLoading(true);
      isLoadingRef.current = true;
      setError(null);
      setHasRequested((current) => current || Boolean(query) || hasCoordinates);

      try {
        const payload = await fetchVenues(
          {
            limit: itemsPerPage,
            offset: page * itemsPerPage,
            search: query,
            lat: modeForFetch === "location" && effectiveHasCoordinates ? effectiveLatitude : undefined,
            lng: modeForFetch === "location" && effectiveHasCoordinates ? effectiveLongitude : undefined,
            mode: modeForFetch,
          },
          controller.signal,
        );
        const append = page > 0;

        setVenues((previous) => {
          const next = append ? [...previous, ...payload.venues] : payload.venues;
          const unique = new Map<number, VenueData>();
          next.forEach((venue) => {
            unique.set(venue.id, venue);
          });
          return Array.from(unique.values());
        });

        const hasNextFromTotal =
          typeof payload.total === "number" && payload.total > (page + 1) * itemsPerPage;
        const hasNextFromCount = payload.venues.length === itemsPerPage;
        setHasMore(hasNextFromTotal || hasNextFromCount);
      } catch (requestError) {
        if (controller.signal.aborted) return;
        const fallbackMessage = requestError instanceof Error ? requestError.message : null;
        setError(fallbackMessage && fallbackMessage.trim().length > 0 ? fallbackMessage : messages.errorLoading);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      }
    }

    loadVenues();

    return () => {
      controller.abort();
      isLoadingRef.current = false;
    };
  }, [
    page,
    query,
    latitude,
    longitude,
    itemsPerPage,
    messages.errorLoading,
    searchMode,
    hasCoordinates,
    effectiveLatitude,
    effectiveLongitude,
    effectiveHasCoordinates,
  ]);

  const submitSearch = useCallback(() => {
    const nextQuery = pendingQuery.trim();
    const nextMode: VenueSearchMode | null = nextQuery
      ? detectVenueSearchMode(nextQuery)
      : hasCoordinates
        ? "location"
        : null;

    setPage(0);
    setVenues([]);
    setHasMore(false);
    setQuery(nextQuery);
    setSearchMode(nextMode);
  }, [pendingQuery, hasCoordinates]);

  const clearFilters = useCallback(() => {
    setPendingQueryState("");
    setQuery("");
    setPage(0);
    setVenues([]);
    setHasMore(false);
    setError(null);
    setHasRequested(Boolean(hasCoordinates));
    setSearchMode(hasCoordinates ? "location" : null);
    setIsLoading(false);
    isLoadingRef.current = false;
  }, [hasCoordinates]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    setPage((current) => current + 1);
  }, [hasMore]);

  const requestLocation = useCallback(() => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) return;
    setIsRequestingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        setUserLocation(coords);
        setIsRequestingLocation(false);
        setPage(0);
        setVenues([]);
        setHasMore(false);
        setSearchMode("location");
        setHasRequested(true);
        setError(null);
        persistUserLocation(coords);
      },
      () => {
        setIsRequestingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }, [persistUserLocation]);

  return {
    pendingQuery,
    activeQuery: query,
    setPendingQuery: setPendingQueryState,
    submitSearch,
    clearFilters,
    venues,
    isLoading,
    error,
    hasMore,
    loadMore,
    requestLocation,
    isRequestingLocation,
    hasRequested,
    latitude,
    longitude,
  };
}

function VenueTeaserCard({
  venue,
  messages,
  distanceLabel,
  status,
}: {
  venue: VenueData;
  messages: HomeVenuesMessages;
  distanceLabel: string | null;
  status: { label: string | null; isOpen: boolean };
}) {
  const photos = getVenuePhotos(venue);
  const hasPhotos = photos.length > 0;
  const mapSlide = <MapEmbed venue={venue} messages={messages} />;
  const surface = (venue.surfaceType ?? "").trim();
  const totalCourts = getTotalCourts(venue, venue.courtRows ?? []);
  const address = formatAddress(venue);
  const typeLabel = formatVenueType(venue.type, messages);

  return (
    <article className="flex w-80 flex-none flex-col overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-lg shadow-primary/10">
      <div className="relative h-40 w-full overflow-hidden rounded-2xl bg-muted">
        {hasPhotos ? (
          <VenueCarousel photos={photos} name={venue.name} mapSlide={mapSlide} sizes="320px" />
        ) : (
          mapSlide
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-foreground">{venue.name}</h3>
            {status.label ? (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  status.isOpen
                    ? "border border-green-200 bg-green-50 text-green-700 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-200"
                    : "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                }`}
              >
                {status.label}
              </span>
            ) : null}
          </div>
          {address ? (
            <p className="text-xs text-muted-foreground">
              {address}
              {distanceLabel ? (
                <>
                  {" "}
                  · <span className="font-medium text-foreground">{distanceLabel}</span>
                </>
              ) : null}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {totalCourts ? <span>{messages.totalCourts.replace("{count}", String(totalCourts))}</span> : null}
          {typeLabel ? (
            <span className="inline-flex items-center rounded-full border border-border px-3 py-1 uppercase tracking-wide">
              {typeLabel}
            </span>
          ) : null}
          {venue.isVerified ? (
            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {messages.verifiedBadge}
            </span>
          ) : null}
        </div>

        {surface ? <p className="text-xs text-muted-foreground">{messages.surfaceLabel.replace("{surface}", surface)}</p> : null}

        <div className="mt-auto">
          <Link
            href={buildVenueSeoPath(venue)}
            className="inline-flex items-center justify-center rounded-full border border-border px-4 py-2 text-xs font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {messages.viewDetails}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function VenueDetailsCard({
  venue,
  messages,
  distanceLabel,
  status,
  todaysSessions,
}: {
  venue: VenueData;
  messages: HomeVenuesMessages;
  distanceLabel: string | null;
  status: { label: string | null; isOpen: boolean };
  todaysSessions: OpenPlaySession[];
}) {
  const photos = getVenuePhotos(venue);
  const courtRows = Array.isArray(venue.courtRows) ? venue.courtRows : [];
  const hours = Array.isArray(venue.hours)
    ? venue.hours.filter((entry) => entry && typeof entry.day === "string")
    : [];
  const amenities = Array.isArray(venue.amenities)
    ? venue.amenities.filter((amenity): amenity is string => typeof amenity === "string" && amenity.length > 0)
    : [];
  const filteredAmenities = amenities.filter((amenity) => AMENITIES_OPTIONS.includes(amenity));
  const description = (venue.description ?? "").trim();
  const surface = (venue.surfaceType ?? "").trim();
  const totalCourts = getTotalCourts(venue, courtRows);
  const hasMultipleCourtRows = courtRows.length > 1;
  const availableHours = hours.length > 0;
  const courtsHeading =
    hasMultipleCourtRows && totalCourts
      ? messages.courtsHeadingWithTotal.replace("{count}", String(totalCourts))
      : messages.courtsHeading;
  const typeLabel = formatVenueType(venue.type, messages);
  const hasPhotos = photos.length > 0;
  const mapSlide = <MapEmbed venue={venue} messages={messages} />;

  return (
    <article
      data-venue-id={venue.id}
      className="flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-lg shadow-primary/5 transition"
    >
      <div className="relative h-60 w-full flex-shrink-0 overflow-hidden rounded-3xl bg-muted">
        {hasPhotos ? <VenueCarousel photos={photos} name={venue.name} mapSlide={mapSlide} /> : mapSlide}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <header className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-semibold text-foreground">{venue.name}</h3>
            {venue.isVerified ? (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {messages.verifiedBadge}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {formatAddress(venue)}
            {distanceLabel ? (
              <>
                {" "}
                · <span className="font-medium text-foreground">{distanceLabel}</span>
              </>
            ) : null}
          </p>
          <div className="flex flex-wrap gap-2 pt-1 text-xs font-medium text-muted-foreground">
            {typeLabel ? (
              <span className="inline-flex items-center rounded-full border border-border px-3 py-1 uppercase tracking-wide">
                {typeLabel}
              </span>
            ) : null}
            {status.label ? (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 ${
                  status.isOpen
                    ? "border border-green-200 bg-green-50 text-green-700 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-200"
                    : "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                }`}
              >
                {status.label}
              </span>
            ) : null}
          </div>
        </header>

        {description ? <p className="line-clamp-3 text-sm text-muted-foreground">{description}</p> : null}

        <div className="space-y-4 text-sm">
          {(courtRows.length > 0 || (totalCourts ?? 0) > 0) && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{courtsHeading}</h4>
              {courtRows.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {courtRows.map((row, index) => (
                    <li key={`${row.courtType}-${row.environment}-${index}`} className="text-muted-foreground">
                      {row.environment} · {row.courtType} · {row.quantity}
                    </li>
                  ))}
                </ul>
              ) : totalCourts ? (
                <p className="mt-2 text-muted-foreground">
                  {messages.courtsFallback.replace("{count}", String(totalCourts))}
                </p>
              ) : null}
            </div>
          )}

          {availableHours ? (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{messages.hoursHeading}</h4>
              <ul className="mt-2 space-y-1">
                {hours.map((entry, index) => (
                  <li key={`${entry.day}-${index}`} className="flex justify-between gap-4 text-muted-foreground">
                    <span>{entry.day}</span>
                    <span className="text-right">
                      {entry.closed ? messages.hoursClosed : `${entry.open} – ${entry.close}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {surface ? (
            <p className="text-muted-foreground">{messages.surfaceLabel.replace("{surface}", surface)}</p>
          ) : null}

          {filteredAmenities.length > 0 ? (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{messages.amenitiesHeading}</h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {filteredAmenities.map((amenity) => (
                  <span
                    key={amenity}
                    className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {todaysSessions.length > 0 ? (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{messages.openPlayHeading}</h4>
              <ul className="mt-2 space-y-1">
                {todaysSessions.map((session) => (
                  <li key={session.id} className="flex items-center justify-between rounded-lg bg-muted px-3 py-2">
                    <span className="text-sm font-medium text-foreground">{formatTimeRange(session.start, session.end)}</span>
                    <span className="text-xs text-muted-foreground">
                      {session.level} · {session.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="mt-auto flex flex-wrap gap-3 pt-2 text-xs text-muted-foreground">
          {venue.phone ? (
            <a className="hover:text-primary" href={`tel:${venue.phone}`}>
              {messages.contactPhone.replace("{phone}", venue.phone)}
            </a>
          ) : null}
          {venue.website ? (
            <a className="hover:text-primary" target="_blank" rel="noreferrer" href={venue.website}>
              {messages.visitWebsite}
            </a>
          ) : null}
          {venue.googleMapLinkAddress ? (
            <a className="hover:text-primary" target="_blank" rel="noreferrer" href={venue.googleMapLinkAddress}>
              {messages.viewOnMap}
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function formatAddress(venue: VenueData) {
  const parts = [venue.address, venue.city, venue.state, venue.zipCode].filter(Boolean);
  return parts.join(", ");
}

function getVenuePhotos(venue: VenueData): string[] {
  if (!Array.isArray(venue.photos) || typeof venue.id !== "number") return [];
  return venue.photos
    .map((photo) => resolveVenuePhotoUrl(venue.id, photo))
    .filter((url): url is string => Boolean(url));
}

function resolveVenuePhotoUrl(venueId: number, photo: unknown): string | null {
  if (typeof photo !== "string") return null;
  const trimmed = photo.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const filename = trimmed.replace(/^\/+/, "");
  const idPart = `c${venueId}`;
  return `${VENUE_PHOTO_BASE_URL}${idPart}/${filename}`;
}

function getTotalCourts(venue: VenueData, rows: CourtConfiguration[]) {
  if (typeof venue.courts === "number" && venue.courts > 0) {
    return venue.courts;
  }

  if (!Array.isArray(rows) || rows.length === 0) return null;

  const sum = rows.reduce((total, row) => {
    const quantity = Number(row.quantity);
    return Number.isFinite(quantity) ? total + quantity : total;
  }, 0);

  return sum > 0 ? sum : null;
}

function getDistanceMiles(venue: VenueData, latitude: number | null, longitude: number | null): number | null {
  if (typeof venue.distance === "number") {
    return venue.distance;
  }
  if (latitude === null || longitude === null || !venue.latitude || !venue.longitude) {
    return null;
  }
  return haversineMiles(latitude, longitude, venue.latitude, venue.longitude);
}

function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 3958.8;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round((R * c + Number.EPSILON) * 10) / 10;
}

function toRadians(value: number) {
  return (value * Math.PI) / 180;
}

export function formatDistance(distance: number) {
  if (distance < 1) {
    return `${Math.round(distance * 5280)} ft`;
  }
  if (distance < 10) {
    return `${distance.toFixed(1)} mi`;
  }
  return `${Math.round(distance)} mi`;
}

export function getVenueStatus(
  venue: VenueData,
  messages: HomeVenuesMessages,
): {
  label: string | null;
  isOpen: boolean;
} {
  const status = (venue.status ?? "").toUpperCase();

  if (status === "CLOSED_TEMPORARY") {
    return { label: messages.statusClosedTemporary, isOpen: false };
  }
  if (status === "CLOSED_PERMANENT") {
    return { label: messages.statusClosedPermanent, isOpen: false };
  }
  if (status === "CLOSED_OUT_OF_SEASON") {
    return { label: messages.statusClosedOutOfSeason, isOpen: false };
  }

  const fallback = computeOpenClosedFromHours(venue, messages);


  return fallback.label ? fallback : { label: null, isOpen: false };
}

function computeOpenClosedFromHours(
  venue: VenueData,
  messages: HomeVenuesMessages,
): { label: string | null; isOpen: boolean } {
  if (!Array.isArray(venue.hours) || venue.hours.length === 0) {
    return { label: null, isOpen: false };
  }

  const now = new Date();
  const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();

  const todayHours = venue.hours.find((entry) => entry.day.toLowerCase() === dayName);
  if (!todayHours) {
    return { label: messages.statusUnknown, isOpen: false };
  }

  if (todayHours.closed) {
    return { label: messages.closedStatusToday, isOpen: false };
  }

  const openMinutes = parseTimeToMinutes(todayHours.open);
  const closeMinutes = parseTimeToMinutes(todayHours.close);
  if (openMinutes === null || closeMinutes === null) {
    return { label: messages.statusUnknown, isOpen: false };
  }

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const isOpen = isTimeWithinRange(nowMinutes, openMinutes, closeMinutes);

  if (isOpen) {
    return {
      label: messages.openUntil.replace("{time}", formatMinutes(closeMinutes)),
      isOpen: true,
    };
  }

  if (nowMinutes < openMinutes) {
    return { label: messages.opensAt.replace("{time}", formatMinutes(openMinutes)), isOpen: false };
  }

  return { label: messages.closedForDay, isOpen: false };
}

function parseTimeToMinutes(value: string | undefined) {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

function formatMinutes(minutes: number) {
  const normalized = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const suffix = hours >= 12 ? "PM" : "AM";
  const standardHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${standardHour}:${mins.toString().padStart(2, "0")} ${suffix}`;
}

function isTimeWithinRange(now: number, start: number, end: number) {
  if (start <= end) {
    return now >= start && now < end;
  }
  return now >= start || now < end;
}

export function getTodaysSessions(venue: VenueData): OpenPlaySession[] {
  if (!Array.isArray(venue.openPlay) || venue.openPlay.length === 0) return [];

  const now = new Date();
  const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const today = venue.openPlay.find((entry) => entry.day.toLowerCase() === dayName);
  if (!today) return [];

  return today.sessions
    .filter((session) => {
      const endMinutes = parseTimeToMinutes(session.end);
      if (endMinutes === null) return false;
      return endMinutes >= nowMinutes;
    })
    .slice(0, 3);
}

function formatTimeRange(start: string, end: string) {
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return `${start} - ${end}`;
  return `${formatMinutes(startMinutes)} - ${formatMinutes(endMinutes)}`;
}

function formatVenueType(type: string, messages: HomeVenuesMessages) {
  const lower = (type ?? "").toLowerCase();
  if (lower.includes("public")) return messages.publicVenue;
  if (lower.includes("private")) return messages.privateVenue;
  return type;
}

function VenueCarousel({
  photos,
  name,
  mapSlide,
  sizes,
}: {
  photos: string[];
  name: string;
  mapSlide?: ReactNode;
  sizes?: string;
}) {
  const slideCount = photos.length + (mapSlide ? 1 : 0);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slideCount === 0) {
      setIndex(0);
      return;
    }
    if (index >= slideCount) {
      setIndex(0);
    }
  }, [index, slideCount]);

  const handleNext = useCallback(() => {
    if (slideCount <= 1) return;
    setIndex((current) => (current + 1) % slideCount);
  }, [slideCount]);

  const handlePrev = useCallback(() => {
    if (slideCount <= 1) return;
    setIndex((current) => (current - 1 + slideCount) % slideCount);
  }, [slideCount]);

  if (slideCount === 0) {
    return null;
  }

  if (slideCount === 1 && photos.length === 1) {
    return (
      <div className="relative size-full">
        <Image
          src={photos[0]}
          alt={name}
          fill
          sizes={sizes ?? "(max-width: 1024px) 100vw, 520px"}
          className="object-cover object-center"
          unoptimized
        />
      </div>
    );
  }

  if (slideCount === 1 && mapSlide) {
    return <div className="relative size-full">{mapSlide}</div>;
  }

  const isPhotoSlide = index < photos.length;

  return (
    <div className="relative size-full overflow-hidden">
      {isPhotoSlide ? (
        <Image
          key={photos[index]}
          src={photos[index]}
          alt={`${name} photo ${index + 1}`}
          fill
          sizes={sizes ?? "(max-width: 1024px) 100vw, 520px"}
          className="object-cover object-center"
          unoptimized
        />
      ) : (
        <div className="absolute inset-0">{mapSlide}</div>
      )}

      {slideCount > 1 ? (
        <>
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/60"
            aria-label="Previous slide"
          >
            {"<"}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/60"
            aria-label="Next slide"
          >
            {">"}
          </button>
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1">
            {Array.from({ length: slideCount }).map((_, dotIndex) => (
              <span key={`dot-${dotIndex}`} className={`size-2 rounded-full ${dotIndex === index ? "bg-white" : "bg-white/50"}`} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function MapEmbed({ venue, messages }: { venue: VenueData; messages: HomeVenuesMessages }) {
  if (venue.latitude && venue.longitude) {
    const embedUrl = `https://maps.google.com/maps?q=${venue.latitude},${venue.longitude}&z=15&output=embed`;
    return (
      <iframe
        title={messages.mapFallbackTitle.replace("{name}", venue.name)}
        src={embedUrl}
        className="size-full"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    );
  }

  return (
    <div className="flex size-full items-center justify-center bg-muted text-sm text-muted-foreground">
      {messages.mapFallbackNoLocation}
    </div>
  );
}
